import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GuideStep } from '../components/common/InteractiveGuide';

interface GuideState {
  isVisible: boolean;
  currentStep: number;
  isCompleted: boolean;
  hasShownWelcome: boolean;
  hasBeenClosedInSession: boolean;
}

export const useInteractiveGuide = () => {
  const [guideState, setGuideState] = useState<GuideState>({
    isVisible: false,
    currentStep: 0,
    isCompleted: false,
    hasShownWelcome: false,
    hasBeenClosedInSession: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Reset guide state when user logs in (when pathname changes to protected route)
  useEffect(() => {
    const protectedPaths = ['/dashboard', '/community', '/profile'];
    const isOnProtectedRoute = protectedPaths.some((path) => location.pathname.startsWith(path));

    if (isOnProtectedRoute) {
      // Only reset hasShownWelcome if guide is not currently active
      setGuideState((prev) => ({
        ...prev,
        hasShownWelcome: prev.isVisible ? prev.hasShownWelcome : false,
      }));
    }
  }, [location.pathname]);

  // Check if guide should be shown after welcome popup
  useEffect(() => {
    // Don't start guide if it's already visible or has been closed in this session
    if (guideState.isVisible || guideState.hasBeenClosedInSession) {
      return;
    }

    // Only show on protected routes
    const protectedPaths = ['/dashboard', '/community', '/profile'];
    const isOnProtectedRoute = protectedPaths.some((path) => location.pathname.startsWith(path));

    if (!isOnProtectedRoute) {
      return;
    }

    // لا نبدأ الدليل تلقائياً - سنبدأه فقط عند إغلاق نافذة الترحيب
    // هذا الـ useEffect موجود فقط للتحقق من المسار المحمي
  }, [location.pathname, guideState.isVisible, guideState.hasBeenClosedInSession]);

  // Define comprehensive guide steps (one complete tour)
  const getGuideSteps = useCallback((): GuideStep[] => {
    return [
      // Dashboard steps
      {
        id: 'dashboard-header',
        target: '[data-guide="dashboard-header"]',
        title: 'مرحباً بك في لوحة التحكم',
        description: 'هنا يمكنك رؤية نظرة عامة على تقدمك اليومي وإحصائياتك الشخصية.',
        position: 'bottom',
        page: 'dashboard',
      },
      {
        id: 'stats-cards',
        target: '[data-guide="stats-cards"]',
        title: 'إحصائياتك السريعة',
        description: 'هذه البطاقات تعرض إجمالي عاداتك وأهدافك بطريقة بصرية جذابة.',
        position: 'bottom',
        page: 'dashboard',
      },
      {
        id: 'progress-circles',
        target: '[data-guide="progress-circles"]',
        title: 'مؤشرات التقدم',
        description: 'هنا يمكنك رؤية تقدمك في العادات والأهداف بشكل دائري وواضح.',
        position: 'bottom',
        page: 'dashboard',
      },
      {
        id: 'habits-section',
        target: '[data-guide="habits-section"]',
        title: 'قسم العادات',
        description: 'هنا يمكنك إدارة عاداتك اليومية وتتبع تقدمك في كل عادة.',
        position: 'bottom',
        page: 'dashboard',
      },
      {
        id: 'quick-actions',
        target: '[data-guide="quick-actions"]',
        title: 'الإجراءات السريعة',
        description: 'اضغط هنا لإضافة عادة جديدة أو هدف جديد بسرعة.',
        position: 'left',
        page: 'dashboard',
      },
      {
        id: 'navigation',
        target: '[data-guide="navigation"]',
        title: 'قائمة التنقل',
        description: 'استخدم هذه القائمة للانتقال بين أقسام المنصة المختلفة.',
        position: 'right',
        page: 'navigation',
      },
      // Goals page steps
      {
        id: 'goals-header',
        target: '[data-guide="goals-header"]',
        title: 'صفحة الأهداف',
        description: 'هنا يمكنك إدارة أهدافك وتتبع تقدمك في تحقيقها.',
        position: 'bottom',
        page: 'goals',
      },
      {
        id: 'goals-stats',
        target: '[data-guide="goals-stats"]',
        title: 'إحصائيات الأهداف',
        description: 'هذه البطاقات تعرض معدل الإنجاز والأهداف المكتملة وإجمالي الأهداف.',
        position: 'bottom',
        page: 'goals',
      },
      {
        id: 'goals-filters',
        target: '[data-guide="goals-filters"]',
        title: 'تصفية الأهداف',
        description: 'استخدم هذه الأزرار لتصفية أهدافك حسب الفئة (تعليم، صحة، مهنة، شخصية).',
        position: 'bottom',
        page: 'goals',
      },
      {
        id: 'goals-search',
        target: '[data-guide="goals-search"]',
        title: 'البحث في الأهداف',
        description: 'استخدم هذا الحقل للبحث في أهدافك بسرعة.',
        position: 'bottom',
        page: 'goals',
      },
      {
        id: 'add-goal',
        target: '[data-guide="add-goal"]',
        title: 'إضافة هدف جديد',
        description: 'اضغط هنا لإضافة هدف جديد وتحديد موعد إنجازه.',
        position: 'left',
        page: 'goals',
      },
      {
        id: 'goals-list',
        target: '[data-guide="goals-list"]',
        title: 'قائمة الأهداف',
        description: 'هنا ستظهر جميع أهدافك مع حالة تقدمها ومواعيدها النهائية.',
        position: 'top',
        page: 'goals',
      },
      // Community steps
      {
        id: 'community-header',
        target: '[data-guide="community-header"]',
        title: 'المجتمع',
        description: 'هنا يمكنك مشاركة إنجازاتك والتفاعل مع الآخرين.',
        position: 'bottom',
        page: 'community',
      },
      {
        id: 'create-post',
        target: '[data-guide="create-post"]',
        title: 'إنشاء منشور جديد',
        description: 'اضغط هنا لكتابة منشور جديد ومشاركته مع المجتمع.',
        position: 'bottom',
        page: 'community',
      },
      {
        id: 'posts-feed',
        target: '[data-guide="posts-feed"]',
        title: 'المنشورات',
        description: 'هنا ستظهر منشورات المجتمع ويمكنك التفاعل معها.',
        position: 'top',
        page: 'community',
      },
      // Friends page steps (4 steps) - Expanded
      {
        id: 'friends-header',
        target: '[data-guide="friends-header"]',
        title: 'الأصدقاء',
        description: 'هنا يمكنك إدارة قائمة أصدقائك وإرسال طلبات صداقة.',
        position: 'bottom',
        page: 'friends',
      },
      {
        id: 'friends-search',
        target: '[data-guide="friends-search"]',
        title: 'البحث عن الأصدقاء',
        description: 'استخدم هذا الحقل للبحث عن أصدقاء جدد.',
        position: 'bottom',
        page: 'friends',
      },
      {
        id: 'friends-list',
        target: '[data-guide="friends-list"]',
        title: 'قائمة الأصدقاء',
        description: 'هنا ستظهر جميع أصدقائك وطلبات الصداقة.',
        position: 'top',
        page: 'friends',
      },
      {
        id: 'add-friend',
        target: '[data-guide="add-friend"]',
        title: 'إضافة صديق',
        description: 'اضغط هنا لإرسال طلب صداقة لصديق جديد.',
        position: 'left',
        page: 'friends',
      },
      // Messages page steps
      {
        id: 'messages-header',
        target: '[data-guide="messages-header"]',
        title: 'الدردشات',
        description: 'هنا يمكنك الدردشة مع أصدقائك وتبادل الرسائل.',
        position: 'bottom',
        page: 'messages',
      },
      {
        id: 'conversations-list',
        target: '[data-guide="conversations-list"]',
        title: 'قائمة المحادثات',
        description: 'هنا ستظهر جميع محادثاتك مع الأصدقاء.',
        position: 'left',
        page: 'messages',
      },
      {
        id: 'chat-area',
        target: '[data-guide="chat-area"]',
        title: 'منطقة الدردشة',
        description: 'هنا ستظهر الرسائل ويمكنك كتابة رسائل جديدة.',
        position: 'top',
        page: 'messages',
      },
      // Profile page steps
      {
        id: 'profile-header',
        target: '[data-guide="profile-header"]',
        title: 'الملف الشخصي',
        description: 'هنا يمكنك عرض وتعديل معلوماتك الشخصية.',
        position: 'bottom',
        page: 'profile',
      },
      {
        id: 'profile-avatar',
        target: '[data-guide="profile-avatar"]',
        title: 'الصورة الشخصية',
        description: 'اضغط هنا لتغيير صورتك الشخصية.',
        position: 'right',
        page: 'profile',
      },
    ];
  }, []);

  // Start guide
  const startGuide = useCallback(() => {
    setGuideState((prev) => ({
      ...prev,
      isVisible: true,
      currentStep: 0,
    }));
  }, []);

  // Start guide after welcome popup (called from welcome popup)
  const startGuideAfterWelcome = useCallback(() => {
    // فقط إذا لم يتم إغلاق الدليل في هذه الجلسة
    if (!guideState.hasBeenClosedInSession) {
      console.log('Guide: Starting guide after welcome popup...');
      setTimeout(() => {
        setGuideState((prev) => ({
          ...prev,
          isVisible: true,
          hasShownWelcome: true,
        }));
      }, 500); // تأخير قصير للتأكد من إغلاق نافذة الترحيب
    }
  }, [guideState.hasBeenClosedInSession]);

  // Force start guide (ignores hasBeenClosedInSession)
  const forceStartGuideNow = useCallback(() => {
    console.log('Guide: Force starting guide...');
    setGuideState((prev) => ({
      ...prev,
      isVisible: true,
      hasShownWelcome: true,
      hasBeenClosedInSession: false, // إعادة تعيين هذه الحالة
    }));
  }, []);

  // Close guide
  const closeGuide = useCallback(() => {
    // Mark this page guide as shown
    localStorage.setItem(`guideShown_${location.pathname}`, 'true');
    setGuideState((prev) => ({
      ...prev,
      isVisible: false,
      hasBeenClosedInSession: true, // تم إغلاق الدليل في هذه الجلسة
    }));
  }, [location.pathname]);

  // Complete guide
  const completeGuide = useCallback(() => {
    // لا نحفظ حالة انتهاء الدليل في localStorage
    localStorage.removeItem('interactiveGuideStarted'); // Clear started flag
    localStorage.removeItem('interactiveGuideCurrentStep'); // Clear current step
    setGuideState((prev) => ({
      ...prev,
      isVisible: false,
      isCompleted: true,
      hasBeenClosedInSession: true, // تم إغلاق الدليل في هذه الجلسة
    }));
  }, []);

  // Change step with automatic page navigation
  const changeStep = useCallback(
    (step: number) => {
      const steps = getGuideSteps();
      const newStep = steps[step];

      if (newStep && newStep.page) {
        const currentPage = location.pathname.split('/')[1] || 'dashboard';
        const targetPage = newStep.page;

        console.log(`Guide: Current page: ${currentPage}, Target page: ${targetPage}, Step: ${step}`);

        if (currentPage !== targetPage) {
          console.log(`Guide: Navigating to ${targetPage} page for step ${step}`);

          // Save current step to localStorage before navigation
          localStorage.setItem('interactiveGuideCurrentStep', step.toString());

          // Navigate to the target page using React Router first
          if (targetPage === 'dashboard') {
            navigate('/dashboard');
          } else if (targetPage === 'goals') {
            navigate('/dashboard/goals');
          } else if (targetPage === 'community') {
            navigate('/community');
          } else if (targetPage === 'friends') {
            navigate('/community/friends');
          } else if (targetPage === 'messages') {
            navigate('/community/messages');
          } else if (targetPage === 'profile') {
            navigate('/profile');
          }

          // Update step after navigation with a small delay to ensure page is loaded
          setTimeout(() => {
            setGuideState((prev) => ({
              ...prev,
              currentStep: step,
              isVisible: true, // Ensure guide remains visible
            }));
          }, 100);
        } else {
          // Same page, just update step
          localStorage.setItem('interactiveGuideCurrentStep', step.toString());
          setGuideState((prev) => ({
            ...prev,
            currentStep: step,
          }));
        }
      } else {
        // No page specified, just update step
        localStorage.setItem('interactiveGuideCurrentStep', step.toString());
        setGuideState((prev) => ({
          ...prev,
          currentStep: step,
        }));
      }
    },
    [getGuideSteps, location.pathname, navigate],
  );

  // Reset guide (for testing or if user wants to see it again)
  const resetGuide = useCallback(() => {
    localStorage.removeItem('interactiveGuideStarted');
    localStorage.removeItem('interactiveGuideCurrentStep');
    localStorage.removeItem('welcomePopupShown');
    setGuideState({
      isVisible: false,
      currentStep: 0,
      isCompleted: false,
      hasShownWelcome: false,
      hasBeenClosedInSession: false,
    });
  }, []);

  // Force start guide (for testing)
  const forceStartGuide = useCallback(() => {
    localStorage.setItem('welcomePopupShown', 'true');
    setGuideState((prev) => ({
      ...prev,
      isVisible: true,
      hasShownWelcome: true,
    }));
  }, []);

  // Reset and start guide (for platform guide button)
  const resetAndStartGuide = useCallback(() => {
    // إعادة تعيين جميع حالات الدليل
    localStorage.removeItem('interactiveGuideCompleted');
    localStorage.removeItem('interactiveGuideStarted');
    localStorage.removeItem('interactiveGuideCurrentStep');
    localStorage.removeItem('welcomePopupShown');

    // إعادة تعيين حالة الدليل
    setGuideState({
      isVisible: false,
      currentStep: 0,
      isCompleted: false,
      hasShownWelcome: false,
      hasBeenClosedInSession: false,
    });

    // بدء الدليل بعد تأخير قصير
    setTimeout(() => {
      setGuideState((prev) => ({
        ...prev,
        isVisible: true,
        hasShownWelcome: true,
      }));
    }, 1000);
  }, []);

  // Reset guide state only (for external use)
  const resetGuideState = useCallback(() => {
    setGuideState({
      isVisible: false,
      currentStep: 0,
      isCompleted: false,
      hasShownWelcome: false,
      hasBeenClosedInSession: false,
    });
  }, []);

  return {
    isVisible: guideState.isVisible,
    currentStep: guideState.currentStep,
    isCompleted: guideState.isCompleted,
    steps: getGuideSteps(),
    startGuide,
    startGuideAfterWelcome,
    forceStartGuideNow,
    closeGuide,
    completeGuide,
    changeStep,
    resetGuide,
    forceStartGuide,
    resetAndStartGuide,
    resetGuideState,
  };
};
